import { FormikHelpers, FormikProps, useFormik } from 'formik';
import get from 'lodash.get';
import React, { forwardRef, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { array, number, object, string } from 'yup';

import FormikSubmit from 'src/components/FormikSubmit';

import './style.scss';

type EmailOTPType = {
  index: string;
  value: string;
};

export type EmailVerificationFormValues = {
  emailOTP: EmailOTPType[];
};

type EmailVerificationFormProps = {
  verifyCallback: (otp: string) => void;
};

const EmailVerificationForm = forwardRef(
  ({ verifyCallback }: EmailVerificationFormProps, ref: React.Ref<FormikProps<EmailVerificationFormValues>>) => {
    //tslint:disable-next-line:no-any
    const [inputRefs, setInputRefs] = useState<Array<React.RefObject<any>>>([]);
    const initialValues: EmailVerificationFormValues = {
      emailOTP: Array(6)
        .fill(null)
        .map((_, idx) => idx)
        .map(index => ({ index: `${index}`, value: null })),
    };

    const validationSchema = object({
      emailOTP: array().of(
        object({
          index: number(),
          value: string().nullable(false),
        })
      ),
    });

    useEffect(() => {
      setInputRefs(
        Array(6)
          .fill(null)
          .map((_, _idx) => React.createRef())
      );
    }, []);

    useEffect(() => {
      if (inputRefs.length && inputRefs[0] && inputRefs[0].current) {
        inputRefs[0].current.focus();
      }
    }, [inputRefs]);

    const onChange = (event: React.ChangeEvent<{ value: string }>, currentIndex: number) => {
      handleChange(event);
      if (event.target.value && currentIndex < values.emailOTP.length - 1) {
        inputRefs[currentIndex + 1].current.focus();
      }
    };

    const onKeyPress = (event: React.KeyboardEvent<{ value: string }>, currentIndex: number) => {
      const value = values.emailOTP[currentIndex].value;
      if (!value && event.key === 'Backspace' && currentIndex) {
        inputRefs[currentIndex - 1].current.focus();
      }
    };

    const handleFormSubmit = async (
      data: EmailVerificationFormValues,
      actions: FormikHelpers<EmailVerificationFormValues>
    ) => {
      try {
        const emailOtp = data.emailOTP.map(otp => otp.value).join('');
        await verifyCallback(emailOtp);
      } catch (error) {
        actions.setErrors({ emailOTP: get(error, 'response.data.error') });
      } finally {
        actions.setSubmitting(false);
      }
    };

    const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: handleFormSubmit,
      innerRef: ref,
    });

    const { handleSubmit, values, errors, handleChange, isSubmitting } = formik;

    const getError = () => {
      const otpError = get(errors, 'emailOTP', '');

      return typeof otpError === 'string' ? otpError : null;
    };

    return (
      <Form className="email-otp-form" noValidate onSubmit={handleSubmit}>
        <div className={`d-flex mt-2 mx-0`}>
          {values.emailOTP.map((dataItem, index) => (
            <div className="email-otp-col p-1" key={`emailOTP[${index}].value`}>
              <Form.Control
                size="lg"
                type="text"
                maxLength={1}
                autoComplete="off"
                ref={inputRefs[index]}
                className={'text-center'}
                value={dataItem.value || ''}
                name={`emailOTP[${index}].value`}
                onChange={(e: React.ChangeEvent<{ value: string }>) => onChange(e, index)}
                onKeyDown={(e: React.KeyboardEvent<{ value: string }>) => onKeyPress(e, index)}
              />
            </div>
          ))}
        </div>
        <div className="text-danger medium ml-2">{getError()}</div>

        <FormikSubmit
          variant="primary"
          loading={isSubmitting}
          disabled={values.emailOTP.some(val => !val.value) || !!getError()}
          text="Verify"
          className="mt-2 font-weight-bold"
        />
      </Form>
    );
  }
);

export default EmailVerificationForm;
